import React from 'react';

const CookiePolicy: React.FC = () => {
  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-gray-900 text-white">
        <div className="absolute top-0 left-0 right-0 h-1 bg-gradient-to-r from-blue-600 via-purple-600 to-pink-600"></div>
        <div className="max-w-4xl mx-auto px-4 py-16">
          <div className="text-center">
            <img 
              src="/clawbrand_logo_color.png" 
              alt="ClawBrand AI" 
              className="h-16 mx-auto mb-6"
            />
            <h1 className="text-4xl font-bold mb-4">Cookie Policy</h1>
            <p className="text-gray-300">Last updated: {new Date().toLocaleDateString()}</p>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-4xl mx-auto px-4 py-12">
        <div className="bg-white rounded-lg shadow-lg p-8 md:p-12">
          <div className="prose prose-lg max-w-none">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">What Are Cookies?</h2>
            <p className="text-gray-700 mb-6">
              Cookies are small text files that are placed on your computer or mobile device when you visit our website. 
              They are widely used to make websites work more efficiently and provide a better user experience. 
              BrandScapes AI uses cookies to enhance your experience and provide our services effectively.
            </p>

            <h2 className="text-2xl font-bold text-gray-900 mb-4">How We Use Cookies</h2>
            <p className="text-gray-700 mb-4">BrandScapes AI uses cookies for the following purposes:</p>

            <div className="space-y-6 mb-8">
              <div className="bg-blue-50 p-6 rounded-lg">
                <h3 className="text-xl font-semibold text-blue-900 mb-3">🔧 Essential Cookies</h3>
                <p className="text-gray-700">
                  These cookies are necessary for the website to function properly. They enable basic functions like 
                  page navigation, secure area access, and remembering your login status.
                </p>
              </div>

              <div className="bg-purple-50 p-6 rounded-lg">
                <h3 className="text-xl font-semibold text-purple-900 mb-3">📊 Analytics Cookies</h3>
                <p className="text-gray-700">
                  We use these cookies to understand how visitors interact with our website. This helps us improve 
                  our service and provide better content. All data is anonymized.
                </p>
              </div>

              <div className="bg-pink-50 p-6 rounded-lg">
                <h3 className="text-xl font-semibold text-pink-900 mb-3">⚙️ Functionality Cookies</h3>
                <p className="text-gray-700">
                  These cookies remember your preferences and choices to provide a more personalized experience, 
                  such as your language preference or region.
                </p>
              </div>

              <div className="bg-green-50 p-6 rounded-lg">
                <h3 className="text-xl font-semibold text-green-900 mb-3">🎯 Marketing Cookies</h3>
                <p className="text-gray-700">
                  These cookies may be set through our site by our advertising partners to build a profile of your 
                  interests and show you relevant ads on other sites.
                </p>
              </div>
            </div>

            <h2 className="text-2xl font-bold text-gray-900 mb-4">Types of Cookies We Use</h2>
            
            <h3 className="text-xl font-semibold text-gray-800 mb-3">First-Party Cookies</h3>
            <p className="text-gray-700 mb-6">
              These are cookies set by BrandScapes AI directly. They are essential for providing our services and 
              maintaining your session security.
            </p>

            <h3 className="text-xl font-semibold text-gray-800 mb-3">Third-Party Cookies</h3>
            <p className="text-gray-700 mb-4">
              We work with trusted third-party services that may set cookies on our behalf:
            </p>
            <ul className="list-disc pl-6 text-gray-700 mb-6 space-y-2">
              <li><strong>Google Analytics:</strong> For website usage analysis</li>
              <li><strong>Stripe:</strong> For secure payment processing</li>
              <li><strong>Customer Support Tools:</strong> To provide better support services</li>
            </ul>

            <h2 className="text-2xl font-bold text-gray-900 mb-4">Managing Your Cookie Preferences</h2>
            <p className="text-gray-700 mb-4">
              You have control over your cookie preferences. Here's how you can manage them:
            </p>

            <h3 className="text-xl font-semibold text-gray-800 mb-3">Browser Settings</h3>
            <p className="text-gray-700 mb-4">
              Most web browsers allow you to control cookies through their settings:
            </p>
            <ul className="list-disc pl-6 text-gray-700 mb-6 space-y-2">
              <li>Chrome: Settings → Privacy and security → Cookies and other site data</li>
              <li>Firefox: Settings → Privacy & Security → Cookies and Site Data</li>
              <li>Safari: Preferences → Privacy → Cookies and website data</li>
              <li>Edge: Settings → Privacy, search, and services → Cookies and site permissions</li>
            </ul>

            <div className="bg-yellow-50 border-l-4 border-yellow-400 p-6 mb-8">
              <h3 className="text-lg font-semibold text-yellow-800 mb-2">⚠️ Important Note</h3>
              <p className="text-gray-700">
                Disabling certain cookies may impact the functionality of BrandScapes AI. Essential cookies cannot 
                be disabled as they are necessary for the platform to function properly.
              </p>
            </div>

            <h2 className="text-2xl font-bold text-gray-900 mb-4">Cookie Duration</h2>
            <p className="text-gray-700 mb-4">
              Cookies can be either "session" or "persistent" cookies:
            </p>
            <ul className="list-disc pl-6 text-gray-700 mb-6 space-y-2">
              <li><strong>Session Cookies:</strong> Deleted when you close your browser</li>
              <li><strong>Persistent Cookies:</strong> Remain on your device for a set period or until manually deleted</li>
            </ul>

            <h2 className="text-2xl font-bold text-gray-900 mb-4">Updates to This Policy</h2>
            <p className="text-gray-700 mb-6">
              We may update this Cookie Policy from time to time to reflect changes in our practices or for legal 
              reasons. We will notify you of any significant changes through our website or via email.
            </p>

            <h2 className="text-2xl font-bold text-gray-900 mb-4">Contact Us</h2>
            <p className="text-gray-700 mb-4">
              If you have questions about our use of cookies or this policy, please contact us:
            </p>
            <div className="bg-gradient-to-r from-blue-50 to-purple-50 p-6 rounded-lg">
              <p className="text-gray-700">
                <strong>Email:</strong> support@brandscapesai.com<br />
                <strong>Website:</strong> Contact form on our website
              </p>
            </div>

            <div className="mt-12 p-6 bg-gray-100 rounded-lg">
              <p className="text-sm text-gray-600 text-center">
                By using BrandScapes AI, you consent to our use of cookies as described in this policy. 
                This policy is effective as of {new Date().toLocaleDateString()}.
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Footer */}
      <footer className="bg-gray-900 text-gray-300 mt-16">
        <div className="absolute top-0 left-0 right-0 h-1 bg-gradient-to-r from-blue-600 via-purple-600 to-pink-600"></div>
        <div className="max-w-7xl mx-auto px-4 py-8">
          <div className="text-center">
            <p className="text-sm">
              © {new Date().getFullYear()} BrandScapes AI. All rights reserved.
            </p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default CookiePolicy;
